import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import '../styles/BookDetail.css';
import CoverImage from './CoverImage';
import ShelfBrowse from './ShelfBrowse';
import { isNarrationSupported, speak, stopNarration } from '../services/narration';
import { joinBiblioPresence, leaveBiblioPresence, subscribeToRealtime } from '../services/realtime';

const ADVANCED_SEARCH_STORAGE_KEY = 'advancedSearchStateV2';
const INITIAL_HOLDINGS_VISIBLE = 12;
const BLURB_CACHE_MAX = 200;
const blurbCache = new Map();
const blurbCacheSet = (isbn, value) => {
  if (blurbCache.size >= BLURB_CACHE_MAX) {
    blurbCache.delete(blurbCache.keys().next().value);
  }
  blurbCache.set(isbn, value);
};

const extractIsbnForLookup = (isbn = '') => {
  const normalized = String(isbn).toUpperCase().replace(/[^0-9X]/g, '');
  const match13 = normalized.match(/[0-9]{13}/);
  if (match13) {
    return match13[0];
  }
  const match10 = normalized.match(/[0-9]{9}[0-9X]/);
  return match10 ? match10[0] : '';
};

// Used only when the catalog record itself has no notes — fetches a free, unauthenticated
// description from Open Library (ISBN -> edition -> work -> description), so a missing blurb
// doesn't have to mean an empty page.
const fetchAutoBlurb = async (rawIsbn) => {
  const isbn = extractIsbnForLookup(rawIsbn);
  if (!isbn) {
    return '';
  }

  if (blurbCache.has(isbn)) {
    return blurbCache.get(isbn);
  }

  try {
    const editionRes = await fetch(`https://openlibrary.org/isbn/${isbn}.json`);
    if (!editionRes.ok) {
      blurbCacheSet(isbn, '');
      return '';
    }
    const edition = await editionRes.json();
    const workKey = edition?.works?.[0]?.key;
    if (!workKey) {
      blurbCacheSet(isbn, '');
      return '';
    }

    const workRes = await fetch(`https://openlibrary.org${workKey}.json`);
    if (!workRes.ok) {
      blurbCacheSet(isbn, '');
      return '';
    }
    const work = await workRes.json();
    const description = typeof work.description === 'string'
      ? work.description
      : (work.description?.value || '');

    blurbCacheSet(isbn, description);
    return description;
  } catch (err) {
    blurbCacheSet(isbn, '');
    return '';
  }
};

const splitAuthors = (author = '') => String(author)
  .split(/\s+and\s+|;/i)
  .map((part) => part.trim())
  .filter(Boolean);

const firstIsbn = (isbn = '') => String(isbn)
  .split('|')[0]
  .replace(/[()]/g, ' ')
  .trim();

const toCoins = (book = {}) => {
  const parts = [
    'ctx_ver=Z39.88-2004',
    'rft_val_fmt=info:ofi/fmt:kev:mtx:book',
    'rft.genre=book',
    `rft.btitle=${encodeURIComponent(book.title || 'Untitled')}`,
    `rft.au=${encodeURIComponent(book.author || 'Unknown')}`,
    `rft.date=${encodeURIComponent(book.copyrightdate || '')}`,
    `rft.isbn=${encodeURIComponent(firstIsbn(book.isbn || ''))}`,
    `rft_id=${encodeURIComponent(`info:koha/biblionumber/${book.biblio_id || ''}`)}`,
  ];

  return parts.filter((part) => !part.endsWith('=')).join('&');
};

function BookDetail() {
  const { biblioId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [book, setBook] = useState(null);
  const [relatedBooks, setRelatedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllHoldings, setShowAllHoldings] = useState(false);
  const [isReadingAloud, setIsReadingAloud] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [autoBlurb, setAutoBlurb] = useState('');

  const handleBack = () => {
    const source = location.state?.from;

    if (
      source === 'book-detail-related' ||
      source === 'book-detail-shelf-browse' ||
      source === 'swipe-discovery' ||
      source === 'not-found-search'
    ) {
      navigate(-1);
      return;
    }

    if (source === 'new-arrivals-page') {
      navigate('/new-arrivals-month-wise');
      return;
    }

    if (source === 'faculty-showcase') {
      navigate('/faculty-showcase');
      return;
    }

    if (source === 'subject-explorer') {
      const { subjectKey, subjectQuery, subjectPage, subjectSort, subjectAvailability, subjectYear } = location.state;
      const params = new URLSearchParams();
      if (subjectQuery) params.append('q', subjectQuery);
      if (subjectPage && subjectPage > 1) params.append('page', subjectPage);
      if (subjectSort && subjectSort !== 'relevance') params.append('sort', subjectSort);
      if (subjectAvailability && subjectAvailability !== 'all') params.append('availability', subjectAvailability);
      if (subjectYear && subjectYear !== 'all') params.append('year', subjectYear);
      
      const queryStr = params.toString();
      navigate(`/subjects/${subjectKey}${queryStr ? '?' + queryStr : ''}`);
      return;
    }

    if (source === 'home-search' || source === 'home-browse') {
      navigate('/');
      return;
    }

    if (source === 'home-new-arrivals-marquee' || source === 'random-roulette') {
      const targetId = source === 'random-roulette' ? 'home-discovery-roulette' : 'home-new-arrivals-marquee';
      navigate('/');
      setTimeout(() => {
        document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
      return;
    }

    if (source === 'advanced-search') {
      navigate('/advanced-search');
      return;
    }

    if (window.history.length > 2) {
      navigate(-1);
      return;
    }

    if (sessionStorage.getItem(ADVANCED_SEARCH_STORAGE_KEY)) {
      navigate('/advanced-search');
      return;
    }

    navigate('/');
  };

  useEffect(() => {
    // Scroll to top when component mounts or biblioId changes
    setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 0);

    const fetchBookDetails = async () => {
      try {
        setLoading(true);
        const [bookResponse, relatedResponse] = await Promise.all([
          fetch(`/api/books/${biblioId}`),
          fetch(`/api/books/${biblioId}/related?limit=6`),
        ]);

        if (!bookResponse.ok) {
          throw new Error('Failed to fetch book details');
        }

        const data = await bookResponse.json();
        setBook(data);

        if (relatedResponse.ok) {
          const relatedData = await relatedResponse.json();
          setRelatedBooks(relatedData.books || []);
        } else {
          setRelatedBooks([]);
        }
      } catch (err) {
        console.error('Error fetching book details:', err);
        setError('Unable to load book details');
      } finally {
        setLoading(false);
      }
    };

    if (biblioId) {
      fetchBookDetails();
    }
  }, [biblioId]);

  useEffect(() => {
    setShowAllHoldings(false);
  }, [biblioId]);

  useEffect(() => {
    setAutoBlurb('');
    if (book && !book.notes && book.isbn) {
      let cancelled = false;
      fetchAutoBlurb(book.isbn).then((description) => {
        if (!cancelled) {
          setAutoBlurb(description);
        }
      });
      return () => {
        cancelled = true;
      };
    }
    return undefined;
  }, [book]);

  useEffect(() => () => stopNarration(), []);

  useEffect(() => {
    if (!biblioId) {
      return undefined;
    }

    setViewerCount(0);
    joinBiblioPresence(biblioId);

    const unsubscribe = subscribeToRealtime((message) => {
      if (message.type === 'presence' && String(message.biblioId) === String(biblioId)) {
        setViewerCount(message.count);
      }
    });

    return () => {
      leaveBiblioPresence(biblioId);
      unsubscribe();
    };
  }, [biblioId]);

  useEffect(() => {
    if (!book) {
      return undefined;
    }

    const addedMetaTags = [];
    const addMeta = (name, content) => {
      const value = String(content || '').trim();
      if (!value) {
        return;
      }

      const tag = document.createElement('meta');
      tag.setAttribute('name', name);
      tag.setAttribute('content', value);
      tag.setAttribute('data-opac-zotero', 'true');
      document.head.appendChild(tag);
      addedMetaTags.push(tag);
    };

    document.title = `${book.title || 'Book'} | OPAC`;

    addMeta('citation_title', book.title);
    splitAuthors(book.author).forEach((author) => addMeta('citation_author', author));

    const isbn = firstIsbn(book.isbn);
    if (isbn) {
      addMeta('citation_isbn', isbn);
    }

    addMeta('citation_publication_date', book.copyrightdate);
    addMeta('citation_date', book.copyrightdate);
    addMeta('citation_technical_report_number', book.biblio_id);

    return () => {
      addedMetaTags.forEach((tag) => {
        if (tag.parentNode) {
          tag.parentNode.removeChild(tag);
        }
      });
    };
  }, [book]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'Available': return 'status-available';
      case 'Checked out': return 'status-checkedout';
      case 'Not for loan': return 'status-notforloan';
      case 'Lost': return 'status-lost';
      case 'Damaged': return 'status-damaged';
      case 'Withdrawn': return 'status-withdrawn';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="book-detail-container">
        <button className="back-btn" onClick={handleBack}>← Back</button>
        <div className="loading">Loading book details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="book-detail-container">
        <button className="back-btn" onClick={handleBack}>← Back</button>
        <div className="error">{error}</div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="book-detail-container">
        <button className="back-btn" onClick={handleBack}>← Back</button>
        <div className="error">Book not found</div>
      </div>
    );
  }

  const holdings = Array.isArray(book.items) ? book.items : [];
  const hasMoreHoldings = holdings.length > INITIAL_HOLDINGS_VISIBLE;
  const visibleHoldings = showAllHoldings ? holdings : holdings.slice(0, INITIAL_HOLDINGS_VISIBLE);

  const handleReadAloud = () => {
    if (isReadingAloud) {
      stopNarration();
      setIsReadingAloud(false);
      return;
    }

    const parts = [book.title];
    if (book.author) {
      parts.push(`by ${book.author}`);
    }
    if (book.notes) {
      parts.push(book.notes);
    }

    const started = speak(parts.join('. '), { onEnd: () => setIsReadingAloud(false) });
    setIsReadingAloud(started);
  };

  return (
    <div className="book-detail-container">
      <span className="coins-metadata" title={toCoins(book)} aria-hidden="true" />
      <button className="back-btn" onClick={handleBack}>← Back</button>
      
      <div className="book-detail-wrapper">
        {/* Left Column: Cover Image */}
        <div className="book-cover-section">
          <div className="book-trailer-teaser" tabIndex={0}>
            <CoverImage
              biblioId={biblioId}
              isbn={book.isbn}
              alt={book.title}
              className="book-cover-large"
              width={300}
              height={450}
              sizeCode="L"
              placeholderLabel="No Cover"
            />
          </div>
        </div>

        {/* Right Column: Bibliographic Information */}
        <div className="book-info-section">
          <h1 className="book-title">{book.title}</h1>

          {viewerCount > 1 && (
            <p className="viewer-presence" aria-live="polite">
              <span className="viewer-presence__dot" aria-hidden="true" /> {viewerCount} people viewing this now
            </p>
          )}

          {isNarrationSupported() && (
            <button type="button" className="read-aloud-btn" onClick={handleReadAloud}>
              {isReadingAloud ? '⏹ Stop reading' : '🔊 Read aloud'}
            </button>
          )}

          <div className="biblio-details">
            {book.author && (
              <div className="detail-row">
                <span className="detail-label">Author:</span>
                <span className="detail-value">{book.author}</span>
              </div>
            )}

            {book.isbn && (
              <div className="detail-row">
                <span className="detail-label">ISBN:</span>
                <span className="detail-value">{book.isbn}</span>
              </div>
            )}

            {book.copyrightdate && (
              <div className="detail-row">
                <span className="detail-label">Published:</span>
                <span className="detail-value">{book.copyrightdate}</span>
              </div>
            )}

            {book.item_count !== undefined && (
              <div className="detail-row">
                <span className="detail-label">Total Items:</span>
                <span className="detail-value">{book.item_count}</span>
              </div>
            )}

            <div className="detail-row">
              <span className="detail-label">Biblionumber:</span>
              <span className="detail-value">{book.biblio_id}</span>
            </div>

            {book.notes && (
              <div className="notes-section">
                <h3>Notes</h3>
                <p>{book.notes}</p>
              </div>
            )}

            {!book.notes && autoBlurb && (
              <div className="notes-section notes-section--auto">
                <h3>Description</h3>
                <p>{autoBlurb}</p>
                <span className="notes-section__source">Source: Open Library</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Holdings Section */}
      {holdings.length > 0 && (
        <div className="holdings-section">
          <h2>Holdings & Availability</h2>
          <div className="holdings-table-wrapper">
            <table className="holdings-table">
              <thead>
                <tr>
                  <th>Call Number</th>
                  <th>Barcode</th>
                  <th>Item Type</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Branch</th>
                </tr>
              </thead>
              <tbody>
                {visibleHoldings.map((item) => (
                  <tr key={item.itemnumber} className={`item-row ${getStatusColor(item.status)}`}>
                    <td className="call-number">{item.call_number || '—'}</td>
                    <td className="barcode">{item.barcode}</td>
                    <td className="item-type">{item.item_type_desc || item.item_type || '—'}</td>
                    <td className="location">{item.location || '—'}</td>
                    <td className="status">
                      <span className={`status-badge ${getStatusColor(item.status)}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="branch">{item.homebranch}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {hasMoreHoldings && (
            <div className="holdings-expand-row">
              <button
                type="button"
                className="holdings-view-more-btn"
                onClick={() => setShowAllHoldings((prev) => !prev)}
              >
                {showAllHoldings
                  ? `View less holdings`
                  : `View more holdings (${holdings.length - INITIAL_HOLDINGS_VISIBLE} more)`}
              </button>
            </div>
          )}

          {/* Summary Statistics */}
          <div className="holdings-summary">
            <div className="summary-stat">
              <span className="stat-label">Total Copies:</span>
              <span className="stat-value">{holdings.length}</span>
            </div>
            <div className="summary-stat">
              <span className="stat-label">Available:</span>
              <span className="stat-value available">
                {holdings.filter(i => i.status === 'Available').length}
              </span>
            </div>
            <div className="summary-stat">
              <span className="stat-label">Checked Out:</span>
              <span className="stat-value checkedout">
                {holdings.filter(i => i.status === 'Checked out').length}
              </span>
            </div>
            <div className="summary-stat">
              <span className="stat-label">Other:</span>
              <span className="stat-value">
                {holdings.filter(i => i.status !== 'Available' && i.status !== 'Checked out').length}
              </span>
            </div>
          </div>
        </div>
      )}

      {holdings.length === 0 && (
        <div className="holdings-section">
          <p className="no-items">No items found for this title</p>
        </div>
      )}

      {relatedBooks.length > 0 && (
        <div className="holdings-section related-section">
          <h2>Related Titles</h2>
          <div className="related-grid">
            {relatedBooks.map((related) => (
              <button
                key={related.biblio_id}
                className="related-card"
                onClick={() => navigate(`/book/${related.biblio_id}`, { state: { from: 'book-detail-related' } })}
              >
                <div className="related-cover-wrap">
                  <CoverImage
                    biblioId={related.biblio_id}
                    isbn={related.isbn}
                    alt={related.title}
                    className="related-cover"
                    width={70}
                    height={100}
                    sizeCode="M"
                  />
                </div>
                <div className="related-content">
                  <h4>{related.title}</h4>
                  <p>{related.author || 'N/A'}</p>
                  <span className="related-meta">
                    Available: {related.available_count || 0} / {related.item_count || 0}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <ShelfBrowse biblioId={biblioId} currentTitle={book.title} />
    </div>
  );
}

export default BookDetail;
